import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/store';
import { creativesSurfaceJobs, selectCreativeJobsMap } from '@/store/slices/creativeJobs';

import { useFeatureGate } from '../../../../hooks/useFeatureGate';
import { ROUTE } from '../../../../routes';
import { loadBannerFamily } from '../../../creatives/banner-family-load';
import {
  CREATIVE_CAROUSELS,
  creativesServiceConfigured,
  fetchCreativeFamilyFromService,
  type CreativeFamily,
} from '../../../creatives/creative-api';
import {
  getTenantSourceState,
  type TenantSourceResult,
  type TenantSourceState,
} from '../../../creatives/creatives-tenant-source';
import { isDirty, snapshotFromState } from '../../../creatives/ordered-source';
import { resolveSummary, unaddressableView, type WebsiteCardView } from '../../card-summary';
import { useBrandSettled } from '../../useBrandSettled';
import { creativesCopy } from './creatives-copy';
import { bannerJobRunning, deriveCreativesSummary, type CreativesCardInput } from './creatives-summary';

type Pool = CreativesCardInput['pool'];
type CarouselRead = CreativesCardInput['carousels'][number];

/** What the reads returned, stamped with the brand and flag they were made for. */
type Reads = { key: string; pool: Pool; carousels: CarouselRead[] };

const readsKey = (
  tenantId: number,
  casinoInstanceId: string,
  carouselsOn: boolean,
  domain: string | undefined,
): string => `${tenantId}|${casinoInstanceId}|${carouselsOn ? 'carousels' : 'pool'}|${domain ?? ''}`;

const pendingReads = (key: string, carouselsOn: boolean): Reads => ({
  key,
  pool: { status: 'loading', items: [] },
  carousels: carouselsOn ? CREATIVE_CAROUSELS.map((id) => ({ id, status: 'loading', dirty: false })) : [],
});

/** The pool is the family's first placement, as on the Creatives page; no family means the read failed. */
const poolFrom = (family: CreativeFamily | undefined): Pool =>
  family
    ? {
        status: 'ready',
        items: (family.placements[0]?.items ?? []).map((item) => ({ hasNew: Boolean(item.has_new) })),
      }
    : { status: 'failed', items: [] };

const carouselFrom = (id: CarouselRead['id'], result: TenantSourceResult<TenantSourceState>): CarouselRead => {
  if (result.ok) return { id, status: 'ready', dirty: isDirty(snapshotFromState(result.value)) };
  if (result.reason === 'unavailable') return { id, status: 'unavailable', dirty: false };
  return { id, status: 'failed', dirty: false };
};

/**
 * The Website Creatives card: reads the banner pool and, with `creatives-carousels` on, each carousel's saved state
 * (read only, the same calls the Creatives page opens with), plus the jobs already live in the store for the
 * "Generating" chip. Re-reads on a brand switch or a flag flip; reads of a superseded brand are aborted and dropped.
 */
export const useCreativesCard = (): WebsiteCardView => {
  const { t } = useTranslation();
  const displayBrand = useAppSelector((state) => state.casinoInstance.displayBrand);
  const brandSettled = useBrandSettled();
  const tenantRaw = Number(displayBrand?.tenant_id);
  const tenantId = Number.isFinite(tenantRaw) && tenantRaw > 0 ? tenantRaw : undefined;
  const casinoInstanceId = displayBrand?.id || undefined;
  const domain = displayBrand?.casino_data?.details?.domain;
  const carouselsOn = useFeatureGate().isEnabled('creatives-carousels');

  const [reads, setReads] = React.useState<Reads | null>(null);

  React.useEffect(() => {
    if (tenantId === undefined || casinoInstanceId === undefined) return undefined;
    const key = readsKey(tenantId, casinoInstanceId, carouselsOn, domain);
    const controller = new AbortController();
    const { signal } = controller;
    // Each read lands on its own; a write for a key the card has moved past starts from that key's pending state.
    const land = (patch: Partial<Omit<Reads, 'key'>>): void =>
      setReads((previous) => ({ ...(previous?.key === key ? previous : pendingReads(key, carouselsOn)), ...patch }));

    void (async () => {
      const read = await loadBannerFamily({
        serviceConfigured: creativesServiceConfigured(),
        fetchFromService: () => fetchCreativeFamilyFromService(tenantId, domain, signal),
      });
      if (!signal.aborted) land({ pool: poolFrom(read.ok ? read.family : undefined) });
    })();

    if (carouselsOn) {
      void (async () => {
        const results = await Promise.all(
          CREATIVE_CAROUSELS.map((id) =>
            getTenantSourceState(casinoInstanceId, { kind: 'carousel', source_id: id }, signal),
          ),
        );
        if (!signal.aborted) land({ carousels: CREATIVE_CAROUSELS.map((id, i) => carouselFrom(id, results[i])) });
      })();
    }

    return () => controller.abort();
  }, [tenantId, casinoInstanceId, carouselsOn, domain]);

  const jobsById = useAppSelector(selectCreativeJobsMap);
  const generating = React.useMemo(
    () => tenantId !== undefined && bannerJobRunning(creativesSurfaceJobs(jobsById), tenantId),
    [jobsById, tenantId],
  );

  if (tenantId === undefined || casinoInstanceId === undefined) {
    return unaddressableView(brandSettled, { label: t('Open banners'), to: ROUTE.creatives() });
  }
  // Until this brand's reads land, the card shows them as loading, never the previous brand's.
  const key = readsKey(tenantId, casinoInstanceId, carouselsOn, domain);
  const current = reads?.key === key ? reads : pendingReads(key, carouselsOn);
  return resolveSummary(
    deriveCreativesSummary({ carouselsOn, pool: current.pool, carousels: current.carousels, generating }),
    (c) => creativesCopy(t, c),
    () => undefined,
  );
};
